/* =====================================================================
   AstaLab 2.0 — configurazione ambienti (obiettivo 2 del round 2.0.0-alpha.1)

   Un UNICO punto che legge la configurazione pubblica e decide, in base
   alla sua presenza/assenza, quale ambiente logico siamo:

     development      — build servita da un dev server locale, .env reale
     artifact-preview  — bundle pubblicato come Artifact Claude: nessuna
                         variabile di rete viene mai iniettata qui (vedi
                         build/bundle-artifact.mjs), quindi questa
                         funzione la riconosce SOLO dall'assenza di
                         configurazione di rete valida, mai da un
                         controllo esplicito "if (isArtifact)" sparso nel
                         codice applicativo (vedi backend/bootstrap.js).
     production        — build reale distribuita su un host statico, con
                         SUPABASE_URL/SUPABASE_PUBLISHABLE_KEY iniettate a
                         build time o runtime da un meccanismo del
                         deployment (non di competenza di questo modulo).

   IMPORTANTE — cosa NON deve mai comparire qui: service_role key,
   password del database, token del provider statistico, o qualunque
   altro segreto. Solo SUPABASE_URL e la publishable key (pubblica per
   design: e' la stessa chiave che ogni app Supabase spedisce al
   browser) possono legittimamente vivere in questo file/bundle.
   ===================================================================== */

/* In un progetto con bundler reale queste due righe leggono
   import.meta.env.VITE_SUPABASE_URL / import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY
   (o process.env con altri bundler). Qui, per restare senza dipendenze
   da un framework specifico (coerente con lo stile hand-rolled della
   1.3), il valore viene letto da un oggetto globale opzionale che il
   file HTML ospitante può definire PRIMA di caricare il bundle:

     <script>window.__ASTALAB_ENV__ = { SUPABASE_URL: "...", SUPABASE_PUBLISHABLE_KEY: "..." };</script>

   Il bundle reale (frontend/index.html, build/bundle-real.mjs) inietta
   questo oggetto leggendo .env a build time. Il bundle Artifact
   (build/bundle-artifact.mjs) NON lo definisce mai: resta undefined, e
   questo modulo ricade correttamente sull'assenza di configurazione. */
function readGlobalEnv(){
  if(typeof window !== "undefined" && window.__ASTALAB_ENV__ && typeof window.__ASTALAB_ENV__ === "object"){
    return window.__ASTALAB_ENV__;
  }
  return {};
}

function readProcessEnv(key){
  try{
    // Consente comunque `node tests/*.js` (ambiente Node, senza `window`) di leggere
    // SUPABASE_URL/SUPABASE_PUBLISHABLE_KEY da variabili d'ambiente reali, per i test
    // reali in tests/real-supabase/ (mai per il bundle Artifact, che è browser-only).
    if(typeof process !== "undefined" && process.env && typeof process.env[key] === "string"){
      return process.env[key];
    }
  }catch(e){ /* ignore: `process` non definito in un browser puro */ }
  return undefined;
}

function nonEmptyString(v){
  return typeof v === "string" && v.trim().length > 0 ? v.trim() : null;
}

export function loadEnvConfig(){
  const g = readGlobalEnv();
  const supabaseUrl = nonEmptyString(g.SUPABASE_URL) || nonEmptyString(readProcessEnv("SUPABASE_URL"));
  const supabasePublishableKey =
    nonEmptyString(g.SUPABASE_PUBLISHABLE_KEY) || nonEmptyString(readProcessEnv("SUPABASE_PUBLISHABLE_KEY"));

  const hasNetworkBackend = !!(supabaseUrl && supabasePublishableKey);
  // URL base pubblica per i redirect di Supabase Auth (es. il link di recupero password),
  // opzionale — se assente, SupabaseBackendAdapter la calcola da window.location a runtime (mai
  // un valore fisso "localhost" congelato nel codice sorgente — revisione r1, obiettivo 8).
  const authRedirectUrl = nonEmptyString(g.AUTH_REDIRECT_URL) || nonEmptyString(readProcessEnv("AUTH_REDIRECT_URL"));

  // Etichetta di ambiente puramente informativa (per diagnostica/log, mai per decidere
  // quale BackendAdapter usare — quella decisione dipende SOLO da hasNetworkBackend,
  // vedi backend/bootstrap.js): "production"/"development" sono entrambi ambienti con
  // rete reale disponibile e si distinguono solo per come sono stati costruiti/serviti,
  // non per come questo modulo si comporta.
  let label = "development";
  if(typeof window === "undefined"){
    label = "node";
  } else if(!hasNetworkBackend){
    label = "artifact-preview";
  } else if(g.ASTALAB_ENV_LABEL === "production"){
    label = "production";
  }

  return Object.freeze({
    label,
    supabaseUrl,
    supabasePublishableKey,
    authRedirectUrl,
    hasNetworkBackend,
    appVersion: (typeof window !== "undefined" && window.APP_VERSION) || "unknown"
  });
}
